import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import App from './App.tsx';
import { PostHogIdentifyUser, PostHogPageviews } from './components/PostHogIntegration.tsx';
import { SentryAppBoundary, SentryIdentifyUser } from './components/SentryIntegration.tsx';
import { initSentry } from './lib/sentryClient.ts';
import { isPostHogConfigured } from './lib/posthogClient.ts';
import { startAppUpdateWatcher } from './lib/startAppUpdateWatcher.ts';
import { AppUpdateBanner } from './components/AppUpdateBanner.tsx';
import { BillingGraceBanner } from './components/BillingGraceBanner.tsx';
import {
  hydrateColorSchemePreference,
  initializeColorSchemePreference,
} from './lib/colorSchemePreference.ts';
// Self-hosted (OFL): no requests to Google Fonts (DSGVO), same look on every device.
import '@fontsource-variable/inter';
import '@fontsource-variable/inter/wght-italic.css';
import '@fontsource-variable/jetbrains-mono';
import './index.css';

initSentry();
startAppUpdateWatcher();
initializeColorSchemePreference();
void hydrateColorSchemePreference().catch(() => {
  // Logged-out and temporarily offline sessions keep the local preference.
});

function CoreApp() {
  return (
    <>
      <App />
      <Analytics />
      <SpeedInsights />
    </>
  );
}

function AppWithPostHog() {
  return (
    <>
      <PostHogPageviews />
      <PostHogIdentifyUser />
      <CoreApp />
    </>
  );
}

function AppWithObservability() {
  return (
    <>
      <SentryIdentifyUser />
      {isPostHogConfigured ? <AppWithPostHog /> : <CoreApp />}
    </>
  );
}

const sentryEnabled = typeof __SENTRY_DSN_PUBLIC__ !== 'undefined' && Boolean(__SENTRY_DSN_PUBLIC__);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <SentryAppBoundary>
        <AppUpdateBanner />
        <BillingGraceBanner />
        {isPostHogConfigured ? (
          // Not initialized here: PostHog starts only after login (see startPostHogForUser).
          <PostHogProvider client={posthog}>
            <AppWithObservability />
          </PostHogProvider>
        ) : sentryEnabled ? (
          <AppWithObservability />
        ) : (
          <CoreApp />
        )}
      </SentryAppBoundary>
    </BrowserRouter>
  </StrictMode>,
);
